// src/pages/EventEditor.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, setDoc, addDoc, getDoc, getDocs, where, deleteDoc, onSnapshot, query } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import ConfirmationDialog from '../components/ConfirmationDialog';
import CircuitManager from './CircuitManager';

import { updateLastModified } from '../utils/backup';
import { compressImage } from '../utils/imageCompression';
import '../styles.css';

function EventEditor({ eventId, vehicleId, onClose, vehicles = [], ownerUid = null }) {
  const [name, setName] = useState('');
  const [selectedVehicleIds, setSelectedVehicleIds] = useState(vehicleId ? [vehicleId] : []);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [circuit, setCircuit] = useState('');
  const [circuitSearch, setCircuitSearch] = useState('');
  const [circuitOptions, setCircuitOptions] = useState([]);
  const [image, setImage] = useState('');
  const [notes, setNotes] = useState('');
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [showCircuitDropdown, setShowCircuitDropdown] = useState(false);
  const [vehicleSuggestions, setVehicleSuggestions] = useState([]);
  const [showCircuitManager, setShowCircuitManager] = useState(false);
  const [circuitSuggestions, setCircuitSuggestions] = useState([]);
  const [circuitInputValid, setCircuitInputValid] = useState(true);

  const [initialData, setInitialData] = useState(null);
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  // For shared events, read/write from the owner's account.
  const dataUid = ownerUid || (user ? user.uid : null);

  // Load circuits in real-time
  useEffect(() => {
    if (!user) return;
    const circuitsQuery = query(collection(db, `users/${user.uid}/circuits`));
    const unsubscribe = onSnapshot(circuitsQuery, (snapshot) => {
      const circuits = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const options = [];
      circuits.forEach(circuit => {
        if (circuit.layouts && circuit.layouts.length > 0) {
          circuit.layouts.forEach((layout, index) => {
            options.push({
              id: `${circuit.id}_${index}`,
              name: `${circuit.name} - ${layout.name}`,
              circuitName: circuit.name,
              layout: layout,
              layoutIndex: index
            });
          });
        } else {
          // for old circuits without layouts
          options.push({
            id: circuit.id,
            name: circuit.name,
            circuitName: circuit.name,
            layout: { name: circuit.layout || '', length: circuit.length || '', image: circuit.image, trackMap: circuit.trackMap },
            layoutIndex: 0
          });
        }
      });
      setCircuitOptions(options);
    }, (err) => {
      console.error('Circuit fetch error:', err);
    });
    return () => unsubscribe();
  }, [user]);

  // Load event data if editing
  useEffect(() => {
    if (!user || !eventId || eventId === 'new') {
      setInitialData({ name, vehicleIds: selectedVehicleIds, startDate, endDate, circuit, image, notes });
      if (vehicleId && vehicles.length > 0) {
        const initialVehicle = vehicles.find(v => v.id === vehicleId);
        if (initialVehicle) {
          setSelectedVehicleIds([initialVehicle.id]);
        }
      }
      return;
    }

    const eventRef = doc(db, `users/${dataUid}/events/${eventId}`);
    const unsubscribe = onSnapshot(eventRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setName(data.name || '');
        const loadedVehicleIds = data.vehicleIds || (data.vehicleId ? [data.vehicleId] : []);
        setSelectedVehicleIds(loadedVehicleIds);
        setStartDate(data.startDate || '');
        setEndDate(data.endDate || '');
        setCircuit(data.circuit || '');
        setCircuitSearch(data.circuit || '');
        setImage(data.image || '');
        setNotes(data.notes || '');
        setInitialData({ name: data.name || '', vehicleIds: loadedVehicleIds, startDate: data.startDate || '', endDate: data.endDate || '', circuit: data.circuit || '', image: data.image || '', notes: data.notes || '' });
        // Validate initial values
        validateCircuitInput(data.circuit || '');
      }
    }, (err) => {
      console.error('Event fetch error:', err);
    });

    return () => unsubscribe();
  }, [eventId, user, vehicleId, vehicles]);

  const saveEvent = async () => {
    if (selectedVehicleIds.length === 0 || !circuit) {
      alert('Please select at least one vehicle and a circuit.');
      return;
    }
    const eventData = { name, vehicleId: selectedVehicleIds[0], vehicleIds: selectedVehicleIds, startDate, endDate, circuit, image, notes, timestamp: Date.now() };

    if (eventId === 'new') {
      // Add new event
      const docRef = await addDoc(collection(db, `users/${user.uid}/events`), eventData);
      await updateLastModified(user);
      onClose();
      window.location.href = `/event/${docRef.id}`; // Redirect to new event
    } else {
      setShowConfirmSave(true); // Confirm overwrite for edits
    }
  };

  const confirmSave = async () => {
    const eventData = { name, vehicleId: selectedVehicleIds[0], vehicleIds: selectedVehicleIds, startDate, endDate, circuit, image, notes, timestamp: Date.now() };
    await setDoc(doc(db, `users/${dataUid}/events/${eventId}`), eventData, { merge: true });
    await updateLastModified(user);
    setShowConfirmSave(false);
    onClose();
  };

  const deleteEvent = async () => {
    if (eventId === 'new') return;
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (!user || eventId === 'new') return;

    // Delete event image from Storage if exists
    const eventDoc = await getDoc(doc(db, `users/${dataUid}/events/${eventId}`));
    if (eventDoc.exists() && eventDoc.data().image) {
      const imageRef = ref(storage, eventDoc.data().image);
      try {
        await deleteObject(imageRef);
      } catch (err) {
        console.error('EventEditor.js: Error deleting image:', err);
      }
    }

    // Delete event and associated sessions
    await deleteDoc(doc(db, `users/${dataUid}/events/${eventId}`));
    const sessionsQuery = query(collection(db, `users/${dataUid}/sessions`), where('eventId', '==', eventId));
    const sessionsSnap = await getDocs(sessionsQuery);
    const deletePromises = sessionsSnap.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    await updateLastModified(user);
    setShowConfirmDelete(false);
    onClose();
    window.location.href = '/';
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('File size exceeds 10MB limit');
      return;
    }
    const eventIdToUse = eventId === 'new' ? Date.now().toString() : eventId;
    const oldImage = image;
    const imageRef = ref(storage, `events/${user.uid}/${eventIdToUse}-${Date.now()}`);
    try {
      const compressedFile = await compressImage(file, 'event');
      await uploadBytes(imageRef, compressedFile);
      const imageUrl = await getDownloadURL(imageRef);
      setImage(imageUrl + '?t=' + Date.now());
      // Cache the new image
      caches.open('hot-setup-cache-v5').then(cache => cache.add(imageUrl));
      // Delete old image if exists
      if (oldImage) {
        const oldPath = oldImage.split('/o/')[1].split('?')[0];
        const oldRef = ref(storage, decodeURIComponent(oldPath));
        await deleteObject(oldRef);
      }
    } catch (err) {
      console.error('EventEditor.js: Error uploading image:', err);
      alert('Failed to upload image');
    }
  };

  const handleClose = () => {
    const currentData = { name, vehicleIds: selectedVehicleIds, startDate, endDate, circuit, image, notes };
    if (JSON.stringify(initialData) !== JSON.stringify(currentData)) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    onClose();
  };

  const handleCircuitManagerClose = () => {
    setShowCircuitManager(false);
  };

  const handleCircuitSearch = (e) => {
    const value = e.target.value;
    setCircuitSearch(value);
    updateCircuitSuggestions(value);
    validateCircuitInput(value);
    if (value === '**Open Circuit Manager**') {
      setShowCircuitManager(true);
      setCircuitSearch('');
      setShowCircuitDropdown(false);
      return;
    }
    const selectedCircuit = circuitOptions.find(c => c.name.toLowerCase() === value.toLowerCase());
    if (selectedCircuit) {
      setCircuit(selectedCircuit.name);
    } else {
      setCircuit(value);
    }
  };

  const updateVehicleSuggestions = (inputValue) => {
    // Exclude already-selected vehicles from suggestions.
    const available = vehicles.filter(v => !selectedVehicleIds.includes(v.id));
    let filtered;
    if (!inputValue.trim()) {
      filtered = available.slice(0, 5);
    } else {
      filtered = available
        .filter(v => (v.name || 'Unnamed Vehicle').toLowerCase().includes(inputValue.toLowerCase()))
        .slice(0, 5);
    }
    setVehicleSuggestions(filtered);
    setShowVehicleDropdown(filtered.length > 0);
  };

  const addVehicleId = (id) => {
    setSelectedVehicleIds(prev => (prev.includes(id) ? prev : [...prev, id]));
    setVehicleSearch('');
    setShowVehicleDropdown(false);
    setVehicleSuggestions([]);
  };

  const removeVehicleId = (id) => {
    setSelectedVehicleIds(prev => prev.filter(v => v !== id));
  };

  const getVehicleNameById = (id) => {
    const v = vehicles.find(veh => veh.id === id);
    return v ? (v.name || 'Unnamed Vehicle') : 'Unnamed Vehicle';
  };

  const validateCircuitInput = (inputValue) => {
    if (!inputValue.trim()) {
      setCircuitInputValid(true);
      return;
    }
    const isValid = circuitOptions.some(c => c.name.toLowerCase() === inputValue.toLowerCase());
    setCircuitInputValid(isValid);
  };

  const updateCircuitSuggestions = (inputValue) => {
    let filtered;
    if (!inputValue.trim()) {
      // Show first 6 circuits when no input
      filtered = circuitOptions.slice(0, 6);
    } else {
      filtered = circuitOptions
        .filter(c => c.name.toLowerCase().includes(inputValue.toLowerCase()))
        .slice(0, 6);
    }
    setCircuitSuggestions(filtered);
    setShowCircuitDropdown(filtered.length > 0);
  };

  const handleVehicleSearch = (e) => {
    const value = e.target.value;
    setVehicleSearch(value);
    updateVehicleSuggestions(value);
  };

  return (
    <div className="overlay-card">
      <div className="card-container">
        <div className="page-title">
          <h1>{eventId === 'new' ? 'New Event' : 'Edit Event'}</h1>
          <button onClick={handleClose} className="close-button">✕</button>
        </div>
        <div className="event-editor-details">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Event Name" className="event-editor-input" />
          <div className="search-input-container">
            <input
              value={vehicleSearch}
              onChange={handleVehicleSearch}
              onFocus={() => {
                updateVehicleSuggestions(vehicleSearch);
              }}
              onBlur={() => {
                setTimeout(() => setShowVehicleDropdown(false), 150);
              }}
              placeholder="Add Vehicle"
              className="event-editor-input"
            />
            {showVehicleDropdown && vehicleSuggestions.length > 0 && (
              <div className="search-dropdown">
                {vehicleSuggestions.map((vehicle) => (
                  <div
                    key={vehicle.id}
                    className="search-dropdown-item"
                    onClick={() => addVehicleId(vehicle.id)}
                  >
                    {vehicle.name || 'Unnamed Vehicle'}
                  </div>
                ))}
              </div>
            )}
          </div>
          {selectedVehicleIds.length > 0 && (
            <div className="vehicle-pills">
              {selectedVehicleIds.map(id => (
                <span key={id} className="vehicle-pill">
                  {getVehicleNameById(id)}
                  <button
                    type="button"
                    className="vehicle-pill-remove"
                    onClick={() => removeVehicleId(id)}
                    aria-label={`Remove ${getVehicleNameById(id)}`}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
          <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px', marginLeft: '12px' }}>Start Date</div>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="event-editor-input" style={{ marginTop: '0' }} />
          <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px', marginLeft: '12px' }}>End Date</div>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="event-editor-input" style={{ marginTop: '0' }} />
          <div className="search-input-container">
            <input
              value={circuitSearch}
              onChange={handleCircuitSearch}
              onFocus={() => {
                updateCircuitSuggestions(circuitSearch);
              }}
              onBlur={() => {
                setTimeout(() => setShowCircuitDropdown(false), 150);
              }}
              placeholder="Circuit"
              className="event-editor-input"
              style={{ borderColor: circuitInputValid ? '#ccc' : '#ff4444' }}
            />
            {showCircuitDropdown && circuitSuggestions.length > 0 && (
              <div className="search-dropdown">
                {circuitSuggestions.map((circuit, index) => (
                  <div
                    key={circuit.id}
                    className="search-dropdown-item"
                    onClick={() => {
                      setCircuitSearch(circuit.name);
                      setCircuit(circuit.name);
                      setShowCircuitDropdown(false);
                      setCircuitSuggestions([]);
                      validateCircuitInput(circuit.name);
                    }}
                  >
                    {circuit.name}
                  </div>
                ))}
                <div
                  className="search-dropdown-item circuit-manager-option"
                  onClick={() => {
                    setShowCircuitManager(true);
                    setCircuitSearch('');
                    setShowCircuitDropdown(false);
                  }}
                >
                  **Open Circuit Manager**
                </div>
              </div>
            )}
          </div>
          <div className="tab-param">
            <span>Image</span>
            <input type="file" onChange={handleImageUpload} className="event-editor-input" accept="image/*" />
          </div>
          <div className="image-preview">
            {image && (
              <img src={image} alt="Event" style={{ maxWidth: '90%', margin: '20px', borderRadius: '20px' }} />
            )}
          </div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes" className="event-editor-textarea" style={{ minHeight: '110px' }} />
          <div className="event-editor-actions">
            <button onClick={saveEvent} className="btn btn-save">Save</button>
            {eventId !== 'new' && !ownerUid && (
              <button onClick={deleteEvent} className="btn btn-danger">Delete Event</button>
            )}
          </div>
        </div>
        {showConfirmSave && (
          <ConfirmationDialog
            message="Are you sure you want to overwrite the existing event data?"
            onConfirm={confirmSave}
            onCancel={() => setShowConfirmSave(false)}
          />
        )}
        {showConfirmDelete && (
          <ConfirmationDialog
            message="Are you sure you want to delete this event and all its sessions?"
            onConfirm={confirmDelete}
            onCancel={() => setShowConfirmDelete(false)}
          />
        )}
        {showDiscardConfirm && (
          <ConfirmationDialog
            message="Are you sure you want to discard unsaved changes?"
            onConfirm={confirmDiscard}
            onCancel={() => setShowDiscardConfirm(false)}
          />
        )}
        {showCircuitManager && (
          <div className="circuit-manager-modal-overlay" onClick={(e) => e.stopPropagation()}>
            <div className="circuit-manager-modal" onClick={(e) => e.stopPropagation()}>
              <CircuitManager isModal={true} onClose={handleCircuitManagerClose} />
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default EventEditor;